import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { StableInput, StableTextarea } from '../components/StableInput';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Users,
  MessageSquare,
  BookOpen,
  Search,
  Plus,
  Heart,
  Clock,
  Eye,
  ArrowLeft,
  Send,
  Loader2,
  HelpCircle,
  Trophy,
  Handshake,
  AlertTriangle,
  ChevronRight,
  GraduationCap,
  TrendingUp,
  Filter,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  question: { label: 'Question', icon: HelpCircle, color: 'bg-blue-100 text-blue-700', borderColor: 'border-l-blue-500' },
  challenge: { label: 'Challenge', icon: AlertTriangle, color: 'bg-amber-100 text-amber-700', borderColor: 'border-l-amber-500' },
  win: { label: 'Win', icon: Trophy, color: 'bg-green-100 text-green-700', borderColor: 'border-l-green-500' },
  collaboration: { label: 'Collaboration', icon: Handshake, color: 'bg-purple-100 text-purple-700', borderColor: 'border-l-purple-500' },
};

const DIFFICULTY_COLORS = {
  beginner: 'bg-green-100 text-green-700',
  intermediate: 'bg-amber-100 text-amber-700',
  advanced: 'bg-red-100 text-red-700',
};

export default function CommunityHub() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('discussions');

  // Discussion state
  const [posts, setPosts] = useState([]);
  const [postsTotal, setPostsTotal] = useState(0);
  const [postsPage, setPostsPage] = useState(1);
  const [postsLoading, setPostsLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Single post view
  const [viewingPost, setViewingPost] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [replyLoading, setReplyLoading] = useState(false);

  // New post dialog
  const [newPostOpen, setNewPostOpen] = useState(false);
  const [postForm, setPostForm] = useState({ title: '', content: '', category: 'question', tags: '' });
  const [postSubmitting, setPostSubmitting] = useState(false);

  // Training state
  const [trainings, setTrainings] = useState([]);
  const [trainingsLoading, setTrainingsLoading] = useState(false);
  const [trainingFilter, setTrainingFilter] = useState('all');
  const [trainingSearch, setTrainingSearch] = useState('');
  const [viewingTraining, setViewingTraining] = useState(null);

  // Stats
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    fetchStats();
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user && activeTab === 'discussions') fetchPosts();
  }, [user, activeTab, categoryFilter, postsPage]);

  useEffect(() => {
    if (user && activeTab === 'training') fetchTrainings();
  }, [user, activeTab, trainingFilter]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setStats(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchPosts = async () => {
    setPostsLoading(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const params = new URLSearchParams({ page: postsPage, limit: 15 });
      if (categoryFilter !== 'all') params.set('category', categoryFilter);
      if (searchQuery) params.set('search', searchQuery);
      const res = await fetch(`${API}/api/community/posts?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setPosts(data.posts);
        setPostsTotal(data.total);
      }
    } catch (e) { console.error(e); }
    finally { setPostsLoading(false); }
  };

  const fetchTrainings = async () => {
    setTrainingsLoading(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const params = new URLSearchParams();
      if (trainingFilter !== 'all') params.set('category', trainingFilter);
      if (trainingSearch) params.set('search', trainingSearch);
      const res = await fetch(`${API}/api/community/trainings?${params}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setTrainings(data.trainings);
      }
    } catch (e) { console.error(e); }
    finally { setTrainingsLoading(false); }
  };

  const openPost = async (postId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/posts/${postId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        setViewingPost(data.post);
        setReplies(data.replies);
      }
    } catch (e) { toast.error('Failed to load post'); }
  };

  const openTraining = async (trainingId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/trainings/${trainingId}`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (res.ok) setViewingTraining(await res.json());
    } catch (e) { toast.error('Failed to load training'); }
  };

  const submitPost = async () => {
    if (!postForm.title.trim() || !postForm.content.trim()) { toast.error('Title and content are required'); return; }
    setPostSubmitting(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/posts`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...postForm, tags: postForm.tags ? postForm.tags.split(',').map(t => t.trim()).filter(Boolean) : [] }),
      });
      if (res.ok) {
        toast.success('Post published!');
        setNewPostOpen(false);
        setPostForm({ title: '', content: '', category: 'question', tags: '' });
        fetchPosts();
        fetchStats();
      } else { const e = await res.json(); toast.error(e.detail || 'Failed to create post'); }
    } catch (e) { toast.error('Failed to create post'); }
    finally { setPostSubmitting(false); }
  };

  const submitReply = async () => {
    if (!replyText.trim() || !viewingPost) return;
    setReplyLoading(true);
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/posts/${viewingPost.id}/replies`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: replyText }),
      });
      if (res.ok) {
        const reply = await res.json();
        setReplies(prev => [...prev, reply]);
        setReplyText('');
        setViewingPost(prev => ({ ...prev, reply_count: (prev.reply_count || 0) + 1 }));
        toast.success('Reply posted!');
      }
    } catch (e) { toast.error('Failed to post reply'); }
    finally { setReplyLoading(false); }
  };

  const toggleLike = async (postId) => {
    try {
      const token = localStorage.getItem('pitchpal_token');
      const res = await fetch(`${API}/api/community/posts/${postId}/like`, {
        method: 'POST', headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(prev => prev.map(p => p.id === postId ? { ...p, likes: p.likes + (data.liked ? 1 : -1), liked_by: data.liked ? [...(p.liked_by || []), user.id] : (p.liked_by || []).filter(id => id !== user.id) } : p));
        if (viewingPost?.id === postId) {
          setViewingPost(prev => ({ ...prev, likes: prev.likes + (data.liked ? 1 : -1), liked_by: data.liked ? [...(prev.liked_by || []), user.id] : (prev.liked_by || []).filter(id => id !== user.id) }));
        }
      }
    } catch (e) { console.error(e); }
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;

  // Training detail view
  if (viewingTraining) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-12" data-testid="training-detail-view">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Button variant="ghost" className="mb-4 text-[#5C6B67]" onClick={() => setViewingTraining(null)} data-testid="training-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Training Library
          </Button>
          <article className="bg-white rounded-xl shadow-sm border border-[#E2E8E6] p-6 sm:p-8">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <Badge className={DIFFICULTY_COLORS[viewingTraining.difficulty] || 'bg-stone-100 text-stone-700'}>{viewingTraining.difficulty}</Badge>
              <Badge variant="outline">{viewingTraining.category}</Badge>
              <span className="text-xs text-stone-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {viewingTraining.estimated_read_time} min read</span>
              <span className="text-xs text-stone-400 flex items-center gap-1"><Eye className="w-3 h-3" /> {viewingTraining.views} views</span>
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-2">{viewingTraining.title}</h1>
            <p className="text-[#5C6B67] mb-6">{viewingTraining.description}</p>
            <div className="prose prose-stone prose-sm max-w-none prose-headings:font-heading prose-headings:text-[#0A0A0A] prose-h2:text-xl prose-h3:text-lg prose-strong:text-stone-800 prose-li:text-[#5C6B67]">
              {viewingTraining.content.split('\n').map((line, i) => {
                const trimmed = line.trim();
                if (!trimmed) return <br key={i} />;
                if (trimmed.startsWith('### ')) return <h3 key={i} className="font-heading text-lg font-bold text-[#0A0A0A] mt-6 mb-2">{trimmed.slice(4)}</h3>;
                if (trimmed.startsWith('## ')) return <h2 key={i} className="font-heading text-xl font-bold text-[#0A0A0A] mt-8 mb-3">{trimmed.slice(3)}</h2>;
                if (trimmed.startsWith('**') && trimmed.endsWith('**')) return <p key={i} className="font-semibold text-stone-800 mt-4 mb-1">{trimmed.slice(2, -2)}</p>;
                if (trimmed.startsWith('- ')) return <li key={i} className="text-[#5C6B67] ml-4 mb-1">{trimmed.slice(2).replace(/\*\*(.*?)\*\*/g, '$1')}</li>;
                return <p key={i} className="text-[#5C6B67] mb-2 leading-relaxed">{trimmed.replace(/\*\*(.*?)\*\*/g, '$1')}</p>;
              })}
            </div>
          </article>
        </div>
      </div>
    );
  }

  // Post detail view
  if (viewingPost) {
    const cat = CATEGORY_CONFIG[viewingPost.category] || CATEGORY_CONFIG.question;
    const CatIcon = cat.icon;
    return (
      <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-12" data-testid="post-detail-view">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Button variant="ghost" className="mb-4 text-[#5C6B67]" onClick={() => { setViewingPost(null); setReplies([]); }} data-testid="post-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to Discussions
          </Button>

          <Card className={`bg-white border-l-4 ${cat.borderColor} mb-6`}>
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3 flex-wrap">
                <Badge className={cat.color}><CatIcon className="w-3 h-3 mr-1" />{cat.label}</Badge>
                {viewingPost.tags?.map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}
              </div>
              <h2 className="font-heading text-xl font-bold text-[#0A0A0A] mb-2">{viewingPost.title}</h2>
              <p className="text-[#5C6B67] whitespace-pre-wrap mb-4">{viewingPost.content}</p>
              <div className="flex items-center justify-between text-sm text-[#5C6B67] pt-3 border-t border-stone-100">
                <div className="flex items-center gap-4">
                  <span className="font-medium text-stone-700">{viewingPost.user_name}</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(viewingPost.created_at).toLocaleDateString()}</span>
                </div>
                <button onClick={() => toggleLike(viewingPost.id)} className={`flex items-center gap-1 transition-colors ${viewingPost.liked_by?.includes(user?.id) ? 'text-red-500' : 'text-stone-400 hover:text-red-400'}`} data-testid="post-like-btn">
                  <Heart className={`w-4 h-4 ${viewingPost.liked_by?.includes(user?.id) ? 'fill-current' : ''}`} />{viewingPost.likes || 0}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Replies */}
          <h3 className="font-semibold text-stone-800 mb-3">{replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}</h3>
          <div className="space-y-3 mb-6">
            {replies.map((reply) => (
              <Card key={reply.id} className="bg-white">
                <CardContent className="p-4">
                  <p className="text-[#5C6B67] whitespace-pre-wrap mb-3">{reply.content}</p>
                  <div className="flex items-center justify-between text-xs text-[#5C6B67]">
                    <div className="flex items-center gap-3">
                      <span className="font-medium text-stone-700">{reply.user_name}</span>
                      <span>{new Date(reply.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Reply input */}
          <Card className="bg-white">
            <CardContent className="p-4">
              <StableTextarea
                value={replyText}
                onChange={(f, v) => setReplyText(v)}
                field="reply"
                placeholder="Share your thoughts or advice..."
                rows={3}
                className="mb-3"
                data-testid="reply-input"
              />
              <Button onClick={submitReply} disabled={replyLoading || !replyText.trim()} className="bg-primary hover:bg-primary-800 text-white" data-testid="reply-submit-btn">
                {replyLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                Post Reply
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Training categories for filter
  const trainingCategories = [...new Set(trainings.map(t => t.category))];

  return (
    <div className="min-h-screen bg-[#FAFAFA] pt-20 pb-12" data-testid="community-hub-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-gradient-to-br from-primary to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <Users className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1 className="font-heading text-3xl font-bold text-[#0A0A0A]">Community Hub</h1>
              <p className="text-[#5C6B67]">Connect, learn, and grow with fellow fundraisers</p>
            </div>
          </div>
          {stats && (
            <div className="flex items-center gap-3">
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-primary">{stats.members}</p>
                <p className="text-xs text-[#5C6B67]">Members</p>
              </div>
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-blue-600">{stats.posts}</p>
                <p className="text-xs text-[#5C6B67]">Discussions</p>
              </div>
              <div className="text-center px-4 py-2 bg-white rounded-lg shadow-sm">
                <p className="text-2xl font-bold text-amber-600">{stats.trainings}</p>
                <p className="text-xs text-[#5C6B67]">Trainings</p>
              </div>
            </div>
          )}
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white p-1 shadow-sm w-full sm:w-auto">
            <TabsTrigger value="discussions" className="gap-2 text-xs sm:text-sm" data-testid="tab-discussions">
              <MessageSquare className="w-4 h-4" /> Discussions
            </TabsTrigger>
            <TabsTrigger value="training" className="gap-2 text-xs sm:text-sm" data-testid="tab-training">
              <GraduationCap className="w-4 h-4" /> Training Library
            </TabsTrigger>
          </TabsList>

          {/* ====== DISCUSSIONS TAB ====== */}
          <TabsContent value="discussions" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                {['all', 'question', 'challenge', 'win', 'collaboration'].map((cat) => {
                  const cfg = CATEGORY_CONFIG[cat];
                  return (
                    <Button key={cat} variant={categoryFilter === cat ? 'default' : 'outline'} size="sm"
                      className={categoryFilter === cat ? 'bg-primary text-white' : ''} onClick={() => { setCategoryFilter(cat); setPostsPage(1); }}
                      data-testid={`filter-${cat}`}>
                      {cfg ? <cfg.icon className="w-3.5 h-3.5 mr-1" /> : <Filter className="w-3.5 h-3.5 mr-1" />}
                      {cfg ? cfg.label : 'All'}
                    </Button>
                  );
                })}
              </div>
              <Button onClick={() => setNewPostOpen(true)} className="bg-primary hover:bg-primary-800 text-white" data-testid="new-post-btn">
                <Plus className="w-4 h-4 mr-1" /> New Post
              </Button>
            </div>

            {/* Search */}
            <div className="flex gap-2">
              <StableInput value={searchQuery} onChange={(f, v) => setSearchQuery(v)} field="search" placeholder="Search discussions..." className="flex-1" data-testid="discussion-search" />
              <Button variant="outline" onClick={() => { setPostsPage(1); fetchPosts(); }} data-testid="discussion-search-btn"><Search className="w-4 h-4" /></Button>
            </div>

            {postsLoading ? (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>
            ) : posts.length === 0 ? (
              <Card className="bg-white"><CardContent className="py-12 text-center">
                <MessageSquare className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-medium text-stone-900 mb-2">No discussions yet</h3>
                <p className="text-[#5C6B67] mb-4">Be the first to start a conversation!</p>
                <Button onClick={() => setNewPostOpen(true)} className="bg-primary hover:bg-primary-800 text-white"><Plus className="w-4 h-4 mr-1" /> Start a Discussion</Button>
              </CardContent></Card>
            ) : (
              <div className="space-y-3">
                {posts.map((post) => {
                  const cat = CATEGORY_CONFIG[post.category] || CATEGORY_CONFIG.question;
                  const CatIcon = cat.icon;
                  return (
                    <Card key={post.id} className={`bg-white hover:shadow-md transition-shadow cursor-pointer border-l-4 ${cat.borderColor}`}
                      onClick={() => openPost(post.id)} data-testid={`post-card-${post.id}`}>
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <Badge className={`${cat.color} text-xs`}><CatIcon className="w-3 h-3 mr-1" />{cat.label}</Badge>
                              {post.tags?.slice(0, 3).map((tag, i) => <Badge key={i} variant="outline" className="text-xs">{tag}</Badge>)}
                            </div>
                            <h4 className="font-semibold text-stone-900 mb-1 truncate">{post.title}</h4>
                            <p className="text-sm text-[#5C6B67] line-clamp-2">{post.content}</p>
                          </div>
                          <ChevronRight className="w-5 h-5 text-stone-300 flex-shrink-0 mt-2" />
                        </div>
                        <div className="flex items-center gap-4 mt-3 text-xs text-[#5C6B67]">
                          <span className="font-medium text-[#5C6B67]">{post.user_name}</span>
                          <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{new Date(post.created_at).toLocaleDateString()}</span>
                          <span className="flex items-center gap-1"><MessageSquare className="w-3 h-3" />{post.reply_count || 0}</span>
                          <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{post.likes || 0}</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ====== TRAINING TAB ====== */}
          <TabsContent value="training" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center justify-between">
              <div className="flex gap-2 flex-wrap">
                <Button variant={trainingFilter === 'all' ? 'default' : 'outline'} size="sm"
                  className={trainingFilter === 'all' ? 'bg-primary text-white' : ''} onClick={() => setTrainingFilter('all')}
                  data-testid="training-filter-all">All Topics</Button>
                {trainingCategories.map((cat) => (
                  <Button key={cat} variant={trainingFilter === cat ? 'default' : 'outline'} size="sm"
                    className={trainingFilter === cat ? 'bg-primary text-white' : ''} onClick={() => setTrainingFilter(cat)}
                    data-testid={`training-filter-${cat}`}>{cat}</Button>
                ))}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <StableInput value={trainingSearch} onChange={(f, v) => setTrainingSearch(v)} field="tsearch" placeholder="Search trainings..." className="flex-1 sm:w-64" data-testid="training-search" />
                <Button variant="outline" onClick={fetchTrainings} data-testid="training-search-btn"><Search className="w-4 h-4" /></Button>
              </div>
            </div>

            {trainingsLoading ? (
              <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-primary mx-auto" /></div>
            ) : trainings.length === 0 ? (
              <Card className="bg-white"><CardContent className="py-12 text-center">
                <BookOpen className="w-12 h-12 text-stone-300 mx-auto mb-4" />
                <h3 className="font-medium text-stone-900 mb-2">No trainings found</h3>
                <p className="text-[#5C6B67]">Try adjusting your search or filters.</p>
              </CardContent></Card>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {trainings.map((t) => (
                  <Card key={t.id} className="bg-white hover:shadow-md transition-shadow cursor-pointer group" onClick={() => openTraining(t.id)} data-testid={`training-card-${t.id}`}>
                    <CardContent className="p-5">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className={DIFFICULTY_COLORS[t.difficulty] || 'bg-stone-100 text-stone-700'} data-testid={`training-difficulty-${t.id}`}>{t.difficulty}</Badge>
                        <span className="text-xs text-stone-400 flex items-center gap-1"><Clock className="w-3 h-3" />{t.estimated_read_time} min</span>
                      </div>
                      <h4 className="font-semibold text-stone-900 mb-2 group-hover:text-primary transition-colors line-clamp-2">{t.title}</h4>
                      <p className="text-sm text-[#5C6B67] line-clamp-3 mb-3">{t.description}</p>
                      <div className="flex items-center justify-between">
                        <Badge variant="outline" className="text-xs">{t.category}</Badge>
                        <span className="text-xs text-stone-400 flex items-center gap-1"><Eye className="w-3 h-3" />{t.views || 0} views</span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* New Post Dialog */}
      <Dialog open={newPostOpen} onOpenChange={setNewPostOpen}>
        <DialogContent className="max-w-lg bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Start a Discussion</DialogTitle>
            <DialogDescription>Share a question, challenge, win, or collaboration opportunity with the community.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category</Label>
              <Select value={postForm.category} onValueChange={(v) => setPostForm(prev => ({ ...prev, category: v }))}>
                <SelectTrigger className="mt-1" data-testid="post-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="challenge">Challenge</SelectItem>
                  <SelectItem value="win">Win / Success</SelectItem>
                  <SelectItem value="collaboration">Collaboration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title *</Label>
              <StableInput value={postForm.title} onChange={(f, v) => setPostForm(prev => ({ ...prev, [f]: v }))} field="title" placeholder="What's on your mind?" className="mt-1" data-testid="post-title-input" />
            </div>
            <div>
              <Label>Details *</Label>
              <StableTextarea value={postForm.content} onChange={(f, v) => setPostForm(prev => ({ ...prev, [f]: v }))} field="content" placeholder="Share the details..." rows={5} className="mt-1" data-testid="post-content-input" />
            </div>
            <div>
              <Label>Tags (comma-separated)</Label>
              <StableInput value={postForm.tags} onChange={(f, v) => setPostForm(prev => ({ ...prev, [f]: v }))} field="tags" placeholder="e.g. grants, events, donors" className="mt-1" data-testid="post-tags-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewPostOpen(false)}>Cancel</Button>
            <Button onClick={submitPost} disabled={postSubmitting || !postForm.title.trim() || !postForm.content.trim()} className="bg-primary hover:bg-primary-800 text-white" data-testid="post-submit-btn">
              {postSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
              Publish
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
